import styled from 'styled-components';
import { CategoryMetricsDto } from 'api/models';
import { formatPercentage, formatNumber } from 'utility/utility';
import { useTranslation } from 'react-i18next';

const Container = styled.div`
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #1f2937;
  margin-bottom: 24px;
`;

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CategoryCard = styled.div`
  padding: 16px;
  border: 1px solid #f3f4f6;
  border-radius: 8px;
  transition: border-color 0.2s;

  &:hover {
    border-color: #bfdbfe;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const CategoryInfo = styled.div`
  flex: 1;
`;

const CategoryName = styled.h4`
  font-weight: 500;
  color: #1f2937;
`;

const ProjectName = styled.p`
  font-size: 14px;
  color: #6b7280;
`;

const MetricsInfo = styled.div`
  text-align: right;
`;

const AvailableText = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
`;

const UnitsText = styled.p`
  font-size: 14px;
  color: #6b7280;
`;

const ProgressContainer = styled.div`
  margin-top: 8px;
`;

const ProgressBar = styled.div`
  height: 8px;
  background: #f3f4f6;
  border-radius: 9999px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ width: number }>`
  height: 100%;
  background: #10b981;
  border-radius: 9999px;
  transition: width 0.5s;
  width: ${props => props.width}%;
`;

export const CategoryMetrics = ({ data }: { data: CategoryMetricsDto[] }) => {
  const { t } = useTranslation();

  return (
    <Container>
      <Title>{t('category-metrics:title', 'Category Metrics')}</Title>
      <Grid>
        {data.map(category => (
          <CategoryCard key={category.categoryId}>
            <CardHeader>
              <CategoryInfo>
                <CategoryName>{category.categoryName}</CategoryName>
                <ProjectName>{category.projectName}</ProjectName>
              </CategoryInfo>
              <MetricsInfo>
                <AvailableText>
                  {formatPercentage(category.freeSquareMeters || 0, category.totalSquareMeters || 0)}{' '}
                  {t('category-metrics:available', 'Available')}
                </AvailableText>
                <UnitsText>
                  {formatNumber(category.availableUnits)}{' '}
                  {t('category-metrics:units', 'units')}
                </UnitsText>
              </MetricsInfo>
            </CardHeader>
            <ProgressContainer>
              <ProgressBar>
                <ProgressFill width={((category.soldSquareMeters || 0) / (category.totalSquareMeters || 1)) * 100} />
              </ProgressBar>
            </ProgressContainer>
          </CategoryCard>
        ))}
      </Grid>
    </Container>
  );
};